import React, {useRef, useEffect, useState} from 'react';
import {Box} from '@mui/material';

export interface GraphDataItem {
  data: string;
  color: string;
}

interface ArrBarStripeProps {
  graphData?: GraphDataItem[];
  total: string;
  height?: number;
  gap?: number;
  stripeWidth?: number;
  // New optional "percent" variant for fixed 100-tick percentage bar
  mode?: 'graph' | 'percent';
  percent?: number; // 0-100
  dataTestId: string;
  state?:
    | 'in-progress'
    | 'success'
    | 'failed'
    | 'draft'
    | 'completed'
    | 'approved'
    | 'published'
    | 'indexed'
    | 'copied'
    | 'catalog-complete';
}

const ArrBarStripe: React.FC<ArrBarStripeProps> = ({
  graphData,
  total,
  height = 32,
  gap = 2,
  stripeWidth = 6,
  mode = 'graph',
  percent = 0,
  state = 'draft',
  dataTestId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [stripes, setStripes] = useState<{color: string}[]>([]);
  const [percentStripeWidthPx, setPercentStripeWidthPx] =
    useState<number>(stripeWidth);
  const [percentGapPx, setPercentGapPx] = useState<number>(gap);

  const totalValue = parseFloat(total);
  const getValue = (val: string) => parseFloat(val);

  const calculateStripes = () => {
    if (mode === 'percent') {
      const totalStripes = 100;

      // Calculate stripe widths and gaps for percent mode
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        if (containerWidth > 0) {
          const baseStripe = stripeWidth > 0 ? stripeWidth : 1;
          const baseGap = gap >= 0 ? gap : 0;
          const hasGap = baseGap > 0;
          if (hasGap) {
            // Maintain the ratio: stripeWidth / gap = baseStripe / baseGap
            // Total width = (100 * stripeWidth) + (99 * gap)
            // Since stripeWidth = ratio * gap, we get:
            // Total = (100 * ratio * gap) + (99 * gap) = gap * (100 * ratio + 99)
            const ratio = baseStripe / baseGap;
            const calculatedGap =
              containerWidth / (totalStripes * ratio + (totalStripes - 1));
            const calculatedStripeWidth = calculatedGap * ratio;
            setPercentStripeWidthPx(calculatedStripeWidth);
            setPercentGapPx(calculatedGap);
          } else {
            const calculatedStripeWidth = containerWidth / totalStripes;
            setPercentStripeWidthPx(calculatedStripeWidth);
            setPercentGapPx(0);
          }
        }
      } else {
        setPercentStripeWidthPx(stripeWidth);
        setPercentGapPx(gap);
      }

      // Create array of 100 stripes for structure (colors are handled in render)
      setStripes(Array(100).fill({color: ''}));
      return;
    }

    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;

    // Calculate total possible stripes based on width and gap
    const totalStripes = Math.floor(
      (containerWidth + gap) / (stripeWidth + gap)
    );

    let allStripes: {color: string}[] = [];

    // Only process graphData if it's provided
    if (graphData && graphData.length > 0) {
      const hasValidData =
        totalValue > 0 && graphData.some((item) => getValue(item.data) > 0);

      if (!hasValidData) {
        allStripes = Array(totalStripes).fill({color: '#2d3d55'});
      } else {
        // Distribute stripes proportionally
        const stripesPerSegment = graphData.map((item) =>
          Math.round((getValue(item.data) / totalValue) * totalStripes)
        );

        // Generate stripes array with colors
        graphData.forEach((item, i) => {
          allStripes.push(
            ...Array(stripesPerSegment[i]).fill({color: item.color})
          );
        });

        // Adjust in case rounding left some empty space
        while (allStripes.length < totalStripes) {
          allStripes.push({
            color: graphData[graphData.length - 1]?.color || '#ccc',
          });
        }
      }
    } else {
      // Default to dark grey stripes when graphData is not provided
      allStripes = Array(totalStripes).fill({color: '#2d3d55'});
    }

    setStripes(allStripes);
  };

  useEffect(() => {
    calculateStripes();
    const observer = new ResizeObserver(calculateStripes);
    const target = containerRef.current; // capture reference now
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target); // use captured target
    };
  }, [graphData, total, gap, stripeWidth, mode, percent, state]);

  // For percent mode, use masked background approach for all states to ensure consistent stripe widths
  // Handles both percent > 0 and percent === 0 cases
  if (mode === 'percent') {
    const safePercent = Math.max(0, Math.min(100, Math.round(percent)));
    const completedWidthPercent = safePercent;

    // Calculate actual rendered stripe width based on flexbox layout
    const actualStripeWidth = percentStripeWidthPx;
    const actualGap = percentGapPx > 0 ? percentGapPx : 0.8;

    // Calculate one stripe+gap unit in pixels for mask
    const oneUnitPx = actualStripeWidth + actualGap;
    // Calculate stripe percentage within one unit - use high precision
    const stripePercent =
      oneUnitPx > 0 ? (actualStripeWidth / oneUnitPx) * 100 : 0;

    // Create repeating pattern: stripe visible, gap transparent
    const stripePattern = `repeating-linear-gradient(
      90deg,
      black 0,
      black ${stripePercent.toFixed(6)}%,
      transparent ${stripePercent.toFixed(6)}%,
      transparent 100%
    )`;

    // Determine completed color based on state
    const isInProgress =
      state === 'in-progress' ||
      state === 'approved' ||
      state === 'published' ||
      state === 'catalog-complete' ||
      state === 'copied' ||
      state === 'indexed';
    const isFailed = state === 'failed';
    const isSuccess = state === 'success' || state === 'completed';
    const remainingColor = 'var(--surface-bold, #182231)';

    const completedColorStyle = isInProgress
      ? {
          backgroundImage:
            'linear-gradient(90deg, #008D95 -22.17%, #5265FF 49.38%, #764CB6 120.94%)',
        }
      : isFailed
        ? {
            background: 'var(--icon-critical, #ff7a7a)',
          }
        : isSuccess
          ? {
              background: 'var(--pfp-green-dark, #2dcB92)',
            }
          : {
              background: remainingColor,
            };

    return (
      <Box
        ref={containerRef}
        position="relative"
        width="100%"
        height={`${height}px`}
        sx={{
          display: 'flex',
          width: '100%',
          // Transparent background so gaps are visible (they show the parent container background)
          background: 'transparent',
        }}
      >
        {/* Completed portion background - masked to show stripes */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: `${completedWidthPercent}%`,
            height: '100%',
            ...completedColorStyle,
            maskImage: stripePattern,
            WebkitMaskImage: stripePattern,
            maskSize: `${oneUnitPx.toFixed(3)}px 100%`,
            WebkitMaskSize: `${oneUnitPx.toFixed(3)}px 100%`,
            maskRepeat: 'repeat-x',
            WebkitMaskRepeat: 'repeat-x',
            maskPosition: '0 0',
            WebkitMaskPosition: '0 0',
            zIndex: 0,
          }}
        />
        {/* Remaining portion background - masked to show stripes */}
        <Box
          sx={{
            position: 'absolute',
            left: `${completedWidthPercent}%`,
            top: 0,
            right: 0,
            height: '100%',
            background: remainingColor,
            maskImage: stripePattern,
            WebkitMaskImage: stripePattern,
            maskSize: `${oneUnitPx.toFixed(3)}px 100%`,
            WebkitMaskSize: `${oneUnitPx.toFixed(3)}px 100%`,
            maskRepeat: 'repeat-x',
            WebkitMaskRepeat: 'repeat-x',
            // Offset mask to align with remaining portion's first stripe
            // Calculate offset based on number of completed stripes
            maskPosition: `-${safePercent * oneUnitPx}px 0`,
            WebkitMaskPosition: `-${safePercent * oneUnitPx}px 0`,
            zIndex: 0,
          }}
        />
        {/* Individual stripe elements for structure and test IDs */}
        {stripes.map((_, i) => {
          const gapValue = actualGap;
          return (
            <Box
              key={i}
              data-testid={`strip-${i}`}
              sx={{
                width: `${actualStripeWidth.toFixed(3)}px`,
                height: `${height}px`,
                flex: '0 0 auto',
                zIndex: 1,
                pointerEvents: 'none',
                marginRight:
                  i < stripes.length - 1 ? `${gapValue.toFixed(3)}px` : 0,
              }}
            />
          );
        })}
      </Box>
    );
  }

  // For graph mode, use flexbox
  return (
    <Box
      ref={containerRef}
      display="flex"
      width="100%"
      data-testid={dataTestId}
    >
      {stripes.map((stripe, i) => {
        const isGradient = stripe.color.includes('gradient');
        return (
          <Box
            key={i}
            data-testid={`strip-${i}`}
            sx={{
              width: `${stripeWidth}px`,
              height: `${height}px`,
              ...(isGradient
                ? {backgroundImage: stripe.color}
                : {background: stripe.color}),
              marginRight: i < stripes.length - 1 ? `${gap}px` : 0,
            }}
          />
        );
      })}
    </Box>
  );
};

export default ArrBarStripe;
